import {
	A,
	all,
	ElementRenderer,
	Button,
	Checkbox,
	Div,
	Input,
	Label,
	Form,
	reactive,
	Search,
	Span,
	TD,
	TR,
	prepend,
} from 'alkali'
import { Grid } from 'components/grid/Grid'
import Icon from 'components/Icon'
import './StudyList.scss'
import { selectedOutcomes, groupedInterventions } from 'model/EvidenceSearch'
import {
	activeTab,
	hasMoreDigitized,
	defaultStudyExpanded,
	studyCollection,
	focusedStudyId,
	digitizedStudiesLoaded,
	whenLastLoading,
	selectedStudyData,
	whenDigitizedLoaded,
	onArticleAdded,
	allArticles,
} from 'model/StudyCollection'
import {
	analysisType,
	selectedStudies,
	analysisSelections,
	studySelections,
	metaResults,
	studyFilter,
} from 'model/SearchQuery'
import { analysisResults } from 'model/Analysis'
import SelectivityDropdown from 'components/generic/SelectivityDropdown'
import { InterventionDropdown } from '../filter/InterventionDropdown'
import noty from 'components/generic/noty'
import Study from './Study'
import { default as StudyHeader, selectionChanged } from './StudyHeader'
import { ForestPlot } from './ForestPlot'
import { app } from 'app/app'
import { setSelectAll, singleGroupSelectionVar } from './AutoSelectDialog'
import ThresholdCountDialog from './ThresholdCountDialog'
import { StudyAnalysisModel } from 'model/AnalyzeStudy'
import { onScrollToastRemover } from 'components/generic/Toast'
import { getArticleId, getArticleIdOfVote, screening, voteForArticle } from 'model/Screening'
import { track } from 'components/tracking/track'

let gridVar = reactive()
const localStudyData = all([gridVar, digitizedStudiesLoaded, selectedStudies], (grid, digitizedStudiesLoaded) => {
	let selectedStudies = digitizedStudiesLoaded.filter((study) =>
		grid.selection[study.id] || study.additionalStudies?.some(study => grid.selection[study.id]))
	let minCells = analysisType == 'evidence-table' ? 0 :
		(analysisType == 'cohort' || analysisType == 'auto') ? 1 : 2
	let isComparative = minCells == 2
	let hasVariance
	if (analysisType != 'evidence-table') {
		selectedStudies.some((study) => {
			let row = grid.row(study)
			let data = (row.element || new StudyAnalysisModel(study)).getRowData()
			let count = groupCount(data)
			isComparative = isComparative || count > 1
			return hasVariance && (isComparative || analysisType != 'auto')
		})
		if (!hasVariance)
			return []
		if (isComparative)
			minCells = 2
	}
	function groupCount(data) {
		if (!data?.cells)
			return 0
		let groupsWithData = data.cells.map(cell => {
			let analyzeAs = data.groups.find(group => group.Id == cell.group)?.analyzeAs
			if (analyzeAs && data.isAnalyzable === true)
				hasVariance = true
			return analyzeAs
		}).filter(a => a)
		return new Set(groupsWithData).size
	}

	return all(
		selectedStudies.map((study) => {
			// react to any changes
			let row = grid.row(study)
			return row.element?.outcome
		}),
		() => {
			return selectedStudies
				.map((study) => {
					let row = grid.row(study)
					let data = (row.element || new StudyAnalysisModel(study)).getRowData()
					if (
						groupCount(data) >= minCells
					)
						return data
				})
				.filter((data) => data)
		}
	)
})
selectedStudyData.put(localStudyData)
let allSelected = reactive(false)
let hasRefreshed

export class StudyList extends Grid.with('.study-list.sources-view', {
	selectionMode: 'none',
	deselectOnRefresh: false,
	allowSelectAll: true,
	allowTextSelection: true,
	selected: selectedStudies,
	loadingMessage: 'Loading...',
	noDataTitle: 'No results found',
	noDataMessage: studyFilter.importCollection.to(collection =>
		(collection ? 'You may want to remove your selected collection so you can search the whole database, or try' : 'Try') +
		' adjusting your PICO question or selected filters'),
	noDataImage: true,
	farOffRemoval: Infinity,
	columns: {},
	id: 'study-list-grid',
}) {
	firstRow: boolean
	userSelectedAll: boolean
	ready(options) {
		this.collection = studyCollection
		super.ready(options)

		/*this.on('dgrid-select', event => {
			if (this.reportView)
				event.preventDefault()
		})*/
		this.on('dgrid-select', (event) => {
			selectionChanged.put(false)
		})
		this.on('dgrid-deselect', (event) => {
			for (const row of event.rows) {
				this.onDeselectRow(row)
				if (row.data?.groups?.length > 1) allSelected.put(false)
			}
			selectionChanged.put(false)
		})
		gridVar.put(this)
		this.on('dgrid-refresh-complete', async (event) => {
			if (hasRefreshed) {
				if (selectedStudies.valueOf().length > 0) selectionChanged.put(true)
			} else {
				await this.waitForLoad()
				hasRefreshed = true
			}
			let selectAllMode = analysisSelections.settings.get('autoSelectMode')
			if (app.user?.Settings?.AutomaticAutoSelect && selectAllMode != 'none' && selectedStudies.valueOf()?.initializing) {
				selectedStudies.valueOf().initializing = false
				this.selectAll()
			//	this._fireSelectionEvents()
			}
		})
		this.on('dgrid-error', (event) => {
			if (event.error.status != 401 && event.error.status != 502 && event.error.name != 'Abort')
				alert(event.error.message)
		})
		onArticleAdded((study) => {
			this.row(study.id).element?.onArticleAdded()
		})

		let lastFocusId
		const updateFocusHighlight = () => {
			for (let lastFocusedStudy of this.querySelectorAll('.focused-study')) {
				lastFocusedStudy.classList.remove('focused-study')
			}
			let focusId = focusedStudyId.valueOf()
			if (lastFocusId == focusId || !lastFocusId)
				return lastFocusId = focusId
			lastFocusId = focusId
			let focusedStudy = focusId && this.row(focusId)?.element
			if (focusedStudy) {
				focusedStudy.classList.add('focused-study')
				let studyTop = focusedStudy.offsetTop
				let bottomIdeal
				let body = this.bodyNode
				if (studyTop < body.scrollTop)
					body.scrollTo({ top: studyTop - 40, behavior: 'smooth' })
				else if ((bottomIdeal = studyTop - body.offsetHeight * 0.75 + 80) > body.scrollTop)
					body.scrollTo({ top: bottomIdeal, behavior: 'smooth' })
			}
		}
		focusedStudyId.subscribe(updateFocusHighlight)
		let positions = {}
		let body = this.bodyNode
		let isAutoScrolling
		this.on('scroll', () => {
			if (!isAutoScrolling) {
				positions = {}
				positions[activeTab] = body.scrollTop
			}
		})
		activeTab.subscribe(() => {
			this.updateScrollingSetting()
			if (positions[activeTab] > -1) {
				if (Math.abs(positions[activeTab] - body.scrollTop) > 50) {
					isAutoScrolling = true
					setTimeout(() => {
						body.scrollTo({ top: positions[activeTab], behavior: 'smooth' })
						setTimeout(() => {
							isAutoScrolling = false
						}, 3000)
					}, 1000)
				}
				return
			}
			let lastHeight = body.offsetHeight
			let bodyMiddle = body.scrollTop + lastHeight / 2
			let middleRow, middleRowOffset
			for (let row of this._rows || []) {
				if (row && row.offsetTop > bodyMiddle && middleRow) {
					middleRowOffset = bodyMiddle - middleRow.offsetTop
					break
				}
				middleRow = row
			}
			if (middleRow) {
				isAutoScrolling = true
				setTimeout(() => {
					positions[activeTab] = middleRow.offsetTop - body.offsetHeight / 2 + middleRowOffset
					if (Math.abs(positions[activeTab] - body.scrollTop) > 50) {
						body.scrollTo({ top: positions[activeTab], behavior: 'smooth' })
						setTimeout(() => {
							isAutoScrolling = false
						}, 3000)
					} else
						isAutoScrolling = false
				}, 1000) // in case we need to scroll to focused study
			}
		})
		setSelectAll(() => this.selectAll(), () => this.clearSelection())
	}
	updateScrollingSetting() {
		screening.then(screening =>
			this.allowScrollingPage =
				activeTab == 'report' ? () => false :
				activeTab == 'analyzable' ? () => hasMoreDigitized() :
				(activeTab == 'extract' && screening) ? () => {
					return !screening.votes.every(vote => {
						let voteArticleId = getArticleIdOfVote(vote)
						return allArticles.find(article => getArticleId(article) == voteArticleId)
					})
				} : null).then(allowScroll => {
					if (!allowScroll || allowScroll())
						this._processScroll()
				})
	}
	whenLoaded: Promise<void>
	renderQueryResults(results, beforeNode, options) {
		let whenQueryResults = super.renderQueryResults(results, beforeNode, options)
		this.whenLoaded = whenQueryResults
		return whenQueryResults
	}

	_processScroll(event) {
		let scrollPromise = super._processScroll(event)
		if (scrollPromise) {
			this.whenLoaded = Promise.resolve(this.whenLoaded).then(() => {
				scrollPromise
				onScrollToastRemover()
			})
		}
		return scrollPromise
	}
	/*	allowSelect(row) {
		if (!row.id || !row.data) {
			return true // the header select-all checkbox or the pre-selection before the data has arrived
		}
		let data = row.data
		let analyzableCount = data?.groups.filter(group => group.analyzeAs).length
		return (analysisType == 'direct' || analysisType == 'network') ? (analyzableCount > 1) :
			(analysisType == 'auto') ? false : (analyzableCount > 0)
		return data && data.groups && data.groups.some(group => group.analyzeAs)
	}*/
	refresh() {
		allSelected.put(false)
		this.userSelectedAll = false
		this.firstRow = true
		return super.refresh()
	}
	renderHeader() {
		this.headerNode.append(
			StudyHeader({
				classes: {
					'dgrid-selected': allSelected,
				}
			})
		).onclick = (event) => {
			if (event.target.classList?.contains('study-toggle')) {
				let wasAllSelected = allSelected.valueOf()
				this[wasAllSelected ? 'clearSelection' : 'selectAll']()
				setTimeout(() => {
					track({ event: wasAllSelected ? 'clear-selections' : 'auto-select' })
				})
			}
			if (event.target.classList?.contains('study-expander'))
				this.expandAll(!defaultStudyExpanded.valueOf())
		}
	}
	renderRow(study, options) {
		let allowSelect = this.allowSelect({ id: study.id, data: study })
		if (this.selection[study.id] && !allowSelect)
			this.selection[study.id] = false
		let selected = this.selection[study.id] || study.additionalStudies?.find(study => this.selection[study.id])
		if (selected && !this.selection[study.id]) {
			let allStudySelections = analysisSelections.get('studySelections')
			let studySelections = allStudySelections[study.id] || (allStudySelections[study.id] = {})
			studySelections.publication = allStudySelections[selected.id]?.publication || selected.id
			this.selection[selected.id] = false
			this.selection[study.id] = true
		}
		let row = new Study({ study })
		if (selected) {
			if (this.firstRow) {
				allSelected.put(true)
			}
			row.classList.add('dgrid-selected')
			row.expandToggle(true)
		} else if (this.shouldAutoSelect({ element: row, data: study }) == 1) {
			let autoSelectMode = analysisSelections.settings.get('autoSelectMode')
			if (this.userSelectedAll || (app.user?.Settings?.AutomaticAutoSelect && autoSelectMode && autoSelectMode != 'none')) {
				this.selection[study.id] = true
				row.classList.add('dgrid-selected')
				this.autoWeightStudy(study, autoSelectMode)
			} else {
				allSelected.put(false)
			}
		}
		this.firstRow = false
		return row
		//		return super.renderRow(data, options)
	}
	userSelect(row, value, resetWeight) {
		if (resetWeight) {
			let studySettings = studySelections.property(this.row(row).id)
			let existingWeighting = studySettings.get('weighting')
			if (existingWeighting >= 0 && existingWeighting < 100)
				studySettings.set('weighting', 100)
		}
		let autoSelectMode = analysisSelections.settings.get('autoSelectMode')
		if (autoSelectMode && autoSelectMode != 'none')
			analysisSelections.settings.set('autoSelectMode', 'none')
		return this.select(row, null, value)
	}
	async _select(row, toRow, value) {
		let selected = this.row(row)
		if (this.reportView)
			return
		super._select(row, toRow, value)
		selected.element?.expandToggle(Boolean(this.selection[selected.id]))
	}
	shouldAutoSelect(row) {
		let studyModel, data, study = row.data
		let selectAllMode = analysisSelections.settings.get('autoSelectMode')
		let autoSelectThreshold = (analysisSelections.settings.get('autoSelectThreshold') ?? 20) / 100
		let hasScreening = screening.valueOf()
		let isPositiveAnalysis = Boolean(selectedOutcomes.valueOf()?.[0]?.isPositive)
		let canSelect =
			(!study.designs.includes('Pooled Analysis')) &&
				(studyModel = (row.element || new StudyAnalysisModel(study))).analyzable.valueOf() &&
				(data = studyModel.getRowData()) &&
				data?.cells?.length > (analysisType == 'cohort' || (analysisType == 'auto' && singleGroupSelectionVar.valueOf()) ? 0 : 1) &&
				(isPositiveAnalysis == Boolean(data.row.isPositive || data.row.suggestedPositive)) &&
				!(hasScreening && !voteForArticle(study).valueOf()?.type.includes('Include'))
		return canSelect ? study.matchingScore > autoSelectThreshold ? 1 : 0 : -1
	}
	autoWeightStudy(study, autoSelectMode) {
		if (autoSelectMode && autoSelectMode != 'none') {
			let forStudy = studySelections.property(study.id)
			if (autoSelectMode == 'weighted')
				forStudy.set('weighting', study.score * 100)
			else if (forStudy.get('weighting'))
				forStudy.undefine('weighting')
		}
	}
	async selectAll() {
		if (this.reportView) return
		await whenDigitizedLoaded
		allSelected.put(true)
		this.userSelectedAll = true
		let rowIds = {}
		let autoSelectThreshold = analysisSelections.settings.get('autoSelectThreshold') ?? 20
		let autoSelectMode = analysisSelections.settings.get('autoSelectMode')
		let missedThresholdCount = 0
		for (let study of digitizedStudiesLoaded) {
			let id = study.id
			var row = this.row(id)
			// only select analyzable rows
			rowIds[id] = true
			let shouldAutoSelect = this.shouldAutoSelect({ element: row.element, data: study })
			if (shouldAutoSelect == 1) {
				if (!this.selection[id])
					this._select(id, null, true)
				this.autoWeightStudy(study, autoSelectMode)
			} else if (shouldAutoSelect == 0)
				missedThresholdCount++
		}
		for (let id in this.selection) if (!rowIds[id]) delete this.selection[id] // we do this to clear out pages from previous sorts
		this._fireSelectionEvents()
		const settings = app.user.Settings
		if (missedThresholdCount > Object.keys(this.selection).length) {
			if (settings.Tour?.showThresholdDialog !== false) {
				new ThresholdCountDialog({ missedThresholdCount, autoSelectThreshold }).show()
			}
		}
		this.querySelector('.dgrid-header .study').classList.add('dgrid-selected', 'analyzable')
	}

	async waitForLoad() {
		let whenLoaded
		while (whenLoaded != whenLastLoading) {
			whenLoaded = whenLastLoading
			await whenLastLoading
		}
		while (whenLoaded != this.whenLoaded) {
			whenLoaded = this.whenLoaded
			await whenLoaded
			await delay(100)
		}
	}
	expandAll(expand = true) {
		defaultStudyExpanded.put(expand)
		for (let studyRow of this.querySelectorAll('.dgrid-row')) {
			studyRow.expandToggle(expand)
		}
	}
	reportView: boolean
	forestRenderer: ElementRenderer<any>
	async switchToForestPlot(immediate) {
		if (immediate) {
			this.classList.add('report-view')
			this.classList.add('transitioning-view')
			this.classList.remove('sources-view')
			this.classList.remove('animated-forest-plot')
		} else {
			this.classList.add('animated-forest-plot')
		}
		this.reportView = true
		await this.waitForLoad()
		if (!this.reportView)
			// if we already switched out of this view
			return
		let outcome = (await selectedOutcomes)[0]
		this.allowScrollingPage = () => false
		this.classList.add('report-view')
		this.classList.add('transitioning-view')
		this.classList.remove('sources-view')
		let results
		try {
			results = await metaResults
		} catch (error) {
			noty({
				text: error.toString(),
				layout: 'center',
				type: 'error',
			})
			return
		}
		if (!(results.interventions.length > 0)) {
			return app.user && alert('Must select analyzable studies to do meta analysis')
		}
		let isNetwork =
			analysisType == 'frequentist' ||
			analysisType == 'bayesian' ||
			(analysisType == 'auto' && results.interventions.length > 2)
		if (isNetwork) {
			import('../analysis/GroupsNetwork').then(({ GroupsNetwork }) =>
				this.contentNode.append(
					GroupsNetwork.with({
						graphWidth: this.contentNode.clientWidth,
						graphHeight: this.contentNode.clientHeight,
					})
				)
			)
		}
		if (!this.reportView)
			// if we already switched out of this view
			return
		if (results.isCohort) this.classList.add('cohort-view')
		if (this.forestRenderer) this.forestRenderer.stop()
		this.forestRenderer = new ElementRenderer({
			variable: analysisSelections.settings,
			element: this,
			updateOnStart: false,
			renderUpdate: (settings) => {
				if (this.reportView) {
					for (let confidenceBar of this.querySelectorAll('.confidence-bar')) {
						confidenceBar.remove()
					}
					this.cleanupLegend()
					this.switchToForestPlot()
				}
			},
		})
		let plot = new ForestPlot({
			grid: this,
		})
	}
	switchToSources() {
		if (!this.reportView) return
		this.updateScrollingSetting()
		//this.refresh()
		this.reportView = false
		for (let selectedId in this.selection) {
			if (!this.selection[selectedId]) continue
			let row = this.row(selectedId)
			row.element?.switchToSourceView()
		}
		//analysisResults.updated()

		let groupNetwork = this.querySelector('.treatment-graph')
		if (groupNetwork) groupNetwork.parentNode.removeChild(groupNetwork)
		this.cleanupLegend()
		setTimeout(() => {
			if (this.reportView) return // already switching back
			this.classList.remove('report-view')
			this.classList.remove('cohort-view')
			this.classList.add('sources-view')
			document.querySelector('.meta-analysis').classList.remove('disabled-link')
		}, 900)
	}
	cleanupLegend() {
		this.querySelector('.scale')?.remove()
		this.querySelector('.axis-value')?.remove()
		this.querySelector('.x-axis')?.remove()
		this.querySelector('.master-scale')?.remove()
	}
	onDeselectRow(row) {
		if (!row.element.classList.contains('expanded-row')) this.hideGroupOutcomes(row)
	}
	hideGroupOutcomes(row) {
		let cell = row.element.querySelector('.show-outcomes')
		if (cell) {
			cell.groupsShown = false
			cell.classList.remove('show-outcomes')
			cell.removeChild(cell.querySelector('.group-rows'))
		}
	}
	exclude(study) {
		this.deselect(study)
		let row = this.row(study)
		row.element.classList.add('excluded')
	}

}

function getOutcome(study) {
	let outcome
	if (study.selectedOutcomes?.[0]) {
		let selectedOutcome = study.selectedOutcomes[0]
		outcome = study.outcomes.find(
			(outcome) =>
				outcome.id == selectedOutcome.id &&
				outcome?.time?.Id == (selectedTimepoints.get(study.Id) || selectedOutcome?.time?.Id)
		)
	} else {
		let chosenOutcomes = selectedOutcomes.valueOf().map((outcome) => outcome.id)
		outcome = study.outcomes.find(
			(outcome) =>
				chosenOutcomes.includes(outcome.id) &&
				(!selectedTimepoints.get(study.Id) ||
					selectedTimepoints.get(study.Id) == outcome?.time?.Id)
		)
	}
	return outcome || study.outcomes[0] // TODO: we shouldn't be doing this blindly, only if it is an acceptable substitute
}

function groupShortName(group, groups) {
	return group.OriginalName.slice(0, 5).toUpperCase()
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const truncateTitle = (string) => (string.length > 65 ? string.slice(0, 60) + '...' : string)
